import parse from "html-react-parser";
import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Form, InputGroup, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useRouteLoaderData } from "react-router-dom";

import { useAppContext } from "@/App";
import { SpinnerButton } from "@components/Common/Button";
import { tmrPushImageEditorEvent } from "@components/Counters";
import { useImageCropperModal } from "@components/Fields/ImageCropper";
import {
    WrapperDatePicker,
    WrapperDateRangePicker,
    WrapperDateTimePicker,
    WrapperTimePicker,
} from "@components/Fields/WrapperDates";
import { WrapperMultipleSelect, WrapperSelect, WrapperSuggestedSelect } from "@components/Fields/WrapperSelects";
import { WrapperCheckbox } from "@components/Fields/WrapperSwitches";
import { WrapperCKEditor, WrapperTextArea, WrapperTextInput } from "@components/Fields/WrapperTexts";
import { fieldChange } from "@components/Forms/Handlers";
import { Awesome, Hint, ParseFormErrors } from "@components/Helpers";
import { getVideoInfo, is_str, setProperty } from "@components/Utils";

function LegendInfoPopup({ legendInfo, show, onClose }) {
    if (!show || !legendInfo?.example) {
        return null;
    }

    return (
        <div
            onClick={onClose}
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.7)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 1050,
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    maxWidth: "80vw",
                    maxHeight: "80vh",
                    backgroundColor: "transparent",
                    boxShadow: "none",
                }}
            >
                <img
                    src={legendInfo.example}
                    alt="Example"
                    style={{
                        maxWidth: "80vw",
                        maxHeight: "80vh",
                        display: "block",
                        objectFit: "contain",
                    }}
                />
            </div>
        </div>
    );
}

function LegendInfo({ legendInfo }) {
    const [showPopup, setShowPopup] = useState(false);
    const { t } = useTranslation();

    if (!legendInfo || !legendInfo.text) {
        return null;
    }

    return (
        <>
            <Form.Text muted>
                {parse(legendInfo.text)}{" "}
                {legendInfo.example && (
                    <a
                        href=""
                        onClick={(e) => {
                            e.preventDefault();
                            setShowPopup(true);
                        }}
                    >
                        {t("Пример")}
                    </a>
                )}
            </Form.Text>
            <LegendInfoPopup legendInfo={legendInfo} show={showPopup} onClose={() => setShowPopup(false)} />
        </>
    );
}

export const DevForm = {
    Field: (props) => <FormField {...props} />,
    Button: (props) => <SpinnerButton {...props} />,
    Checkbox: (props) => <WrapperCheckbox {...props} />,
    DatePicker: (props) => <WrapperDatePicker {...props} />,
    DateRangePicker: (props) => <WrapperDateRangePicker {...props} />,
    DateTimePicker: (props) => <WrapperDateTimePicker {...props} />,
    Select: (props) => <WrapperSelect {...props} />,
    SelectMultiple: (props) => <WrapperMultipleSelect {...props} />,
    SelectSuggest: (props) => <WrapperSuggestedSelect {...props} />,
    TextArea: (props) => <WrapperTextArea {...props} />,
    TextInput: (props) => <WrapperTextInput {...props} />,
    TimePicker: (props) => <WrapperTimePicker {...props} />,
};

const selectStyles = (err) => {
    const hasError = () => {
        return err ? { borderColor: "#dc3545" } : {};
    };
    return {
        control: (base) => ({
            ...base,
            ...hasError(),
        }),
    };
};

function FormField({
    prefix,
    apiData,
    setApiData,
    name,
    hideInputs,
    bareControl,
    onChange,
    append,
    prepend,
    asRow,
    hideLabel,
    colLabel,
    colField,
    helpText = null,
    underLabel = null,
    maxLength = null,
    ...props
}) {
    const { t } = useTranslation();
    const { apiData: appApiData, setApiData: appSetApiData } = useAppContext();

    apiData ||= appApiData;
    setApiData ||= appSetApiData;

    if (!apiData?.forms_data) {
        return null;
    }

    const form = apiData.forms_data[prefix];
    const field = form?.fields[name];
    const widget = field?.widget || {};
    const readOnly = widget.attrs?.readonly;
    const mL = maxLength || field.max_length; // можно переопределить в форме (желательно сделать такое для большего кол-во свойств)

    const listOnChange = (newData) => {
        const newApiData = { ...apiData, newData };
        setApiData(newApiData);
    };

    const form_field = (
        <FormFieldControl
            {...props}
            prefix={prefix}
            apiData={apiData}
            setApiData={setApiData}
            name={name}
            onChange={onChange}
            maxLength={mL}
        />
    );
    if (bareControl) {
        return form_field;
    }
    if (widget.is_hidden) {
        return form_field;
    }

    const formFieldGroupProps = {
        label: field?.label,
        required: field?.required,
        asRow,
        hideLabel,
        colLabel,
        colField,
        underLabel,
    };
    switch (widget.input_type) {
        case "ckeditor":
        case "daterange":
        case "datetimerange":
        case "date":
        case "datetime":
        case "time":
        case "radioselect":
        case "select":
        case "selectmultiple":
        case "checkboxmultiple":
        case "switch":
            return (
                <FormFieldGroup {...formFieldGroupProps}>
                    {form_field}
                    <FormFieldErrorMessage error={form.errors[name]} custom={true} />
                    <FormFieldHelpText help_text={helpText || field.help_text} />
                    {widget.attrs.tooltip_data && (
                        <Hint tooltip={widget.attrs.tooltip_data} className="field-right-hint" />
                    )}
                </FormFieldGroup>
            );
        case "checkbox":
            return (
                <FormFieldGroup {...formFieldGroupProps} label="" required={false}>
                    <span style={{ display: "inline-block" }}>{form_field}</span>{" "}
                    <Hint tooltip={widget.attrs.tooltip_data} />
                    <FormFieldErrorMessage error={form.errors[name]} custom={true} />
                    <FormFieldHelpText help_text={helpText || field.help_text} />
                </FormFieldGroup>
            );
        case "file": {
            const delFile = (ev, fn) => {
                ev.preventDefault();
                const newApiData = { ...apiData };
                newApiData.forms_data[prefix].data[fn] = "";
                newApiData.forms_data[prefix].data[`${fn}-clear`] = 1;
                setApiData(newApiData);
            };
            return (
                <FormFieldGroup {...formFieldGroupProps}>
                    {is_str(form.data[name]) &&
                        // приехала ссылка с бэка
                        form.data[name] &&
                        form.data[name] !== "null" &&
                        (field.is_image ? (
                            <div className="field__image">
                                <a href={form.data[name]} target="_blank">
                                    <img src={form.data[name]} className="img-thumbnail" />
                                    {!field.required && !field.disabled && !readOnly && (
                                        <span
                                            className="fa fa-remove field__image-remove"
                                            title={t("Удалить")}
                                            onClick={(ev) => {
                                                delFile(ev, name);
                                            }}
                                        />
                                    )}
                                </a>
                            </div>
                        ) : field.is_video ? (
                            <div className="field__video js-field__video">
                                <video width="400" height="auto" controls="controls">
                                    <source src={form.data[name]} />
                                </video>
                                {!field.disabled && !readOnly && (
                                    <span
                                        className="fa fa-remove field__video-remove"
                                        title={t("Удалить")}
                                        onClick={(ev) => {
                                            delFile(ev, name);
                                        }}
                                    />
                                )}
                            </div>
                        ) : (
                            <a href={form.data[name]} target="_blank">
                                {t("Ссылка")}
                            </a>
                        ))}

                    {!hideInputs && !readOnly && form_field}
                    <FormFieldErrorMessage error={form.errors[name]} custom={true} />
                    <LegendInfo legendInfo={widget?.attrs?.legend_info} />
                    <FormFieldHelpText help_text={helpText || field.help_text} />
                </FormFieldGroup>
            );
        }
        case "text":
        case "number":
        case "textarea":
            return (
                <FormFieldGroup {...formFieldGroupProps}>
                    {prepend || append ? (
                        <InputGroup>
                            {prepend && (
                                <InputGroup.Prepend>
                                    <InputGroup.Text>{prepend}</InputGroup.Text>
                                </InputGroup.Prepend>
                            )}
                            {form_field}
                            {append && (
                                <InputGroup.Append>
                                    <InputGroup.Text>{append}</InputGroup.Text>
                                </InputGroup.Append>
                            )}
                            <FormFieldErrorMessage error={form.errors[name]} />
                        </InputGroup>
                    ) : (
                        <>
                            {form_field}
                            <FormFieldErrorMessage error={form.errors[name]} />
                        </>
                    )}
                    {field.field_class !== "URLField" && (
                        <FormFieldLenCount maxLength={mL} disabled={field.disabled} val={form.data[name]} />
                    )}
                    <FormFieldHelpText help_text={helpText || field.help_text} />
                    <Hint tooltip={widget.attrs.tooltip_data} className="field-right-hint" />
                </FormFieldGroup>
            );
        case "list_input":
            return (
                <FormFieldGroup {...formFieldGroupProps}>
                    {(form.data[name] || [""]).map((o, i) => (
                        <div className="dev-inline-control mb-1" key={`${name}_${i}`}>
                            <DevForm.TextInput
                                size="sm"
                                value={o}
                                placeholder={widget.attrs.placeholder}
                                maxLength={mL}
                                onChange={(ev) => {
                                    const newFormData = { ...apiData.forms_data };
                                    newFormData[prefix].data[name][i] = ev.target.value;
                                    listOnChange(newFormData);
                                }}
                            />
                            {i === 0 && (
                                <Button
                                    variant="light"
                                    size="sm"
                                    onClick={() => {
                                        const newFormData = { ...apiData.forms_data };
                                        newFormData[prefix].data[name].push("");
                                        listOnChange(newFormData);
                                    }}
                                >
                                    <Awesome type="plus" />
                                </Button>
                            )}
                            {i !== 0 && (
                                <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => {
                                        const newFormData = { ...apiData.forms_data };
                                        newFormData[prefix].data[name] = [
                                            ...newFormData[prefix].data[name].slice(0, i),
                                            ...newFormData[prefix].data[name].slice(i + 1),
                                        ];
                                        listOnChange(newFormData);
                                    }}
                                >
                                    <Awesome type="minus" />
                                </Button>
                            )}
                        </div>
                    ))}
                </FormFieldGroup>
            );
        default:
            return (
                <div style={{ color: "red" }}>
                    FormField error for field "{name}", {JSON.stringify(field)}
                </div>
            );
    }
}

function FormFieldControl({ prefix, apiData, setApiData, name, onChange = fieldChange, maxLength, ...props }) {
    const { t } = useTranslation();
    const { showImageCropperModal } = useImageCropperModal();
    const fieldRefs = useRef([]);

    if (!apiData?.forms_data) return null;

    const form = apiData.forms_data[prefix];
    const field = form.fields[name];
    const widget = field?.widget || {};
    const readOnly = widget.attrs?.readonly;
    const field_name = `${prefix}-${name}`;
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");

    if (widget.is_hidden) {
        return <Form.Control type="hidden" name={field_name} value={form.data[name] || ""} />;
    }

    // TODO: попробовать сделать поле name в обертках обязательным
    switch (widget.input_type) {
        case "ckeditor":
            return (
                <div className={`${field_name}-anchr`}>
                    <WrapperCKEditor
                        prefix={prefix}
                        apiData={apiData}
                        setApiData={setApiData}
                        name={name}
                        onChange={onChange}
                    />
                </div>
            );
        case "daterange":
            return (
                <WrapperDateRangePicker
                    value={form.data[name]}
                    placeholder={widget.attrs.placeholder}
                    onChange={(rng) => {
                        const val = Array.isArray(rng) && rng[0] && rng[1] ? [rng[0], rng[1]] : [];
                        onChange({ prefix, apiData, setApiData, name, val });
                    }}
                />
            );
        case "datetimerange":
            return (
                <WrapperDateRangePicker
                    withTime={true}
                    value={form.data[name]}
                    placeholder={widget.attrs.placeholder}
                    onChange={(rng) => {
                        const val = Array.isArray(rng) && rng[0] && rng[1] ? [rng[0], rng[1]] : [];
                        onChange({ prefix, apiData, setApiData, name, val });
                    }}
                />
            );
        case "date":
            if (field.disabled) {
                // Преобразуем дату из формата YYYY-MM-DD в формат DD.MM.YYYY
                const formattedDate = form.data[name] ? form.data[name].split("-").reverse().join(".") : "";

                // костыль, т.к датапикер не поддерживает disabled
                return <Form.Control type="text" value={formattedDate} disabled={true} />;
            }
            return (
                <WrapperDatePicker
                    name={field_name}
                    value={form.data[name]}
                    onChange={(val) => onChange({ prefix, apiData, setApiData, name, val })}
                    readOnly={readOnly}
                    minDate={widget.attrs.min_date}
                />
            );
        case "datetime":
            if (field.disabled) {
                // костыль, т.к датапикер не поддерживает disabled
                return <Form.Control type="text" value={form.data[name]} disabled={true} />;
            }
            return (
                <WrapperDateTimePicker
                    name={field_name}
                    value={form.data[name]}
                    onChange={(val) => onChange({ prefix, apiData, setApiData, name, val })}
                    readOnly={readOnly}
                    minDate={widget.attrs.min_date}
                />
            );
        case "time":
            if (field.disabled) {
                // костыль, т.к датапикер не поддерживает disabled
                return <Form.Control type="text" value={form.data[name]} disabled={true} />;
            }
            return (
                <WrapperTimePicker
                    name={field_name}
                    value={form.data[name]}
                    onChange={(val) => onChange({ prefix, apiData, setApiData, name, val })}
                    readOnly={readOnly}
                />
            );
        case "radioselect": {
            return widget.choices.map((o) => {
                const id = `${field_name}-${o.value}`;
                const value = form.data[name];
                const radioProps = {
                    id,
                    type: "radio",
                    label: o.label,
                    name: field_name,
                    disabled:
                        field.disabled ||
                        (widget.attrs.choices_disabled && widget.attrs.choices_disabled.includes(o.value)),
                    readOnly,
                    checked: value !== undefined && value !== null && value.toString() === o.value.toString(),
                    onChange: () => onChange({ prefix, apiData, setApiData, name, val: o.value }),
                    inline: props.inline,
                };
                if (widget.attrs.label_as_image) {
                    radioProps.inline = true;
                    radioProps.label = (
                        <div>
                            <label>{o.label}</label>
                            <br />
                            <img src={widget.attrs.label_as_image[o.value]} width={250} height={150} />
                        </div>
                    );
                }
                const control = (
                    <>
                        <span style={{ display: "inline-block" }}>
                            <WrapperCheckbox {...radioProps} />
                        </span>{" "}
                        <Hint
                            tooltip={widget.attrs.choices_tooltip_data && widget.attrs.choices_tooltip_data[o.value]}
                        />
                    </>
                );
                return radioProps.inline ? (
                    <React.Fragment key={id}>{control}</React.Fragment>
                ) : (
                    <div key={id}>{control}</div>
                );
            });
        }
        case "switch": {
            const sw = (
                <WrapperCheckbox
                    id={field_name}
                    name={field_name}
                    type={"switch"}
                    disabled={field.disabled}
                    readOnly={readOnly}
                    checked={Boolean(form.data[name])}
                    onChange={(ev) => {
                        onChange({ prefix, apiData, setApiData, name, val: ev.target.checked });
                    }}
                />
            );
            if (props.tooltip) {
                return (
                    <div style={{ display: "flex", alignItems: "center" }}>
                        {sw}
                        <Hint tooltip={props.tooltip} />
                    </div>
                );
            }
            return sw;
        }
        case "checkbox":
            return (
                <WrapperCheckbox
                    id={field_name}
                    name={field_name}
                    label={parse(field.label)}
                    disabled={field.disabled}
                    readOnly={readOnly}
                    checked={Boolean(form.data[name])}
                    onChange={(ev) => {
                        onChange({ prefix, apiData, setApiData, name, val: ev.target.checked });
                    }}
                />
            );
        case "checkboxmultiple":
            return widget.choices.map((o) => {
                let sval = form.data[name] || [];
                if (!Array.isArray(sval)) {
                    // хак, приезжает с бэка не массив при отправке 1 чекбокса (происходит после сабмита)
                    // пофиксить на бэке не видится возможным
                    sval = [sval];
                }
                // приводим все к строкам, чтобы потом сравнивать
                let val = sval.map((e) => e.toString());
                const item_value = o.value.toString();
                const choices_disabled = widget.attrs.choices_disabled
                    ? widget.attrs.choices_disabled.map((e) => e.toString())
                    : [];
                const id = `${prefix}_${name}_${widget.input_type}_${item_value}`;
                return (
                    <WrapperCheckbox
                        key={id}
                        id={id}
                        label={parse(o.label)}
                        name={item_value}
                        disabled={field.disabled || choices_disabled.includes(item_value)}
                        checked={val.includes(item_value)}
                        onChange={(selected) => {
                            const tgt = selected.target;
                            if (tgt.checked && !val.includes(tgt.name)) {
                                val.push(tgt.name);
                            }
                            if (!tgt.checked && val.includes(tgt.name)) {
                                val = val.filter((v) => v !== tgt.name);
                            }
                            onChange({ prefix, apiData, setApiData, name, val });
                        }}
                    />
                );
            });
        case "selectmultiple": {
            const { suggest_url } = field;
            const selectProps = {
                ...props,
                choices: widget.choices,
                name: field_name,
                value: form.data[name],
                placeholder: widget.attrs.placeholder,
                disabled: field.disabled,
                readOnly,
                onChange: (val) => {
                    onChange({ prefix, apiData, setApiData, name, val });
                },
                styles: selectStyles(form.errors[name]),
                sortable: widget.attrs.sortable,
                hiddenChoices: widget.attrs.hidden_choices,
            };
            return suggest_url ? (
                <WrapperSuggestedSelect
                    isMulti={true}
                    creatable={field.creatable}
                    suggestUrl={suggest_url}
                    {...selectProps}
                />
            ) : (
                <WrapperMultipleSelect {...selectProps} />
            );
        }
        case "select": {
            const { suggest_url, default_options } = field;
            const selectProps = {
                ...props,
                name: field_name,
                choices: widget.choices,
                value: form.data[name],
                defaultOptions: default_options,
                placeholder: widget.attrs.placeholder,
                disabled: field.disabled,
                clearable: Boolean(widget.attrs.clearable),
                readOnly,
                onChange: (data) => {
                    if (data && data.value !== form.data[name]) {
                        onChange({
                            prefix,
                            apiData,
                            setApiData,
                            name,
                            val: data && data.value !== undefined && data.value !== null ? data.value : "",
                        });
                    }
                },
                styles: selectStyles(form.errors[name]),
            };
            return suggest_url ? (
                <WrapperSuggestedSelect {...selectProps} suggestUrl={suggest_url} creatable={field.creatable} />
            ) : (
                <WrapperSelect {...selectProps} />
            );
        }
        case "file": {
            const [selectedFileName, setSelectedFileName] = useState("");

            // При выборе файла а потом закрытии модального окна (до загрузки файла) сбрасываем имя файла
            useEffect(() => {
                const handleCloseCropper = (event) => {
                    if (event.detail.fieldName === name) {
                        setSelectedFileName("");
                    }
                };

                window.addEventListener("closeImageCropperModal", handleCloseCropper);
                return () => window.removeEventListener("closeImageCropperModal", handleCloseCropper);
            }, [name]);

            const handleFileClick = () => {
                if (!is_str(form.data[name])) {
                    const newApiData = { ...apiData };
                    newApiData.forms_data[prefix].data[name] = null;
                    setApiData(newApiData);
                }
                fieldRefs.current[field_name].value = "";
                setSelectedFileName(""); // сбрасываем имя файла при нажатии "Выбрать файл"

                const presets = apiData.forms_data[prefix].fields[name].widget.attrs.image_presets;
                if (presets && presets.length === 1 && presets[0].use_cropper) {
                    tmrPushImageEditorEvent("img_chose_file_click", gameCommonInfo, form);
                }
            };

            const handleFileChange = async (ev) => {
                // если удаляется и загружается файл одновременно, то чистку не делаем
                const newApiData = { ...apiData };
                delete newApiData.forms_data[prefix].data[`${name}-clear`];
                setApiData(newApiData);

                // Обновляем имя файла
                if (ev.target.files && ev.target.files.length > 0) {
                    if (widget.attrs.multiple) {
                        setSelectedFileName(
                            Array.from(ev.target.files)
                                .map((f) => f.name)
                                .join(", ")
                        );
                    } else {
                        setSelectedFileName(ev.target.files[0].name);
                    }
                } else {
                    setSelectedFileName("");
                }

                if (field.is_image) {
                    const presets = apiData.forms_data[prefix].fields[name].widget.attrs.image_presets;
                    if (presets && presets.length === 1 && presets[0].use_cropper) {
                        const readFile = (file) => {
                            return new Promise((resolve, reject) => {
                                const reader = new FileReader();
                                reader.onload = (e) => {
                                    const img = new Image();
                                    img.onload = () => {
                                        resolve({
                                            name: file.name,
                                            originalSrc: e.target.result,
                                            img,
                                        });
                                    };
                                    img.onerror = (error) => {
                                        reject(error);
                                    };
                                    img.src = e.target.result;
                                };
                                reader.onerror = (err) => reject(err);
                                reader.readAsDataURL(file);
                            });
                        };

                        const files = Array.from(ev.target.files);
                        if (files.length > 0) {
                            try {
                                const promises = files.map(readFile);
                                const contents = await Promise.all(promises);
                                showImageCropperModal(contents, prefix, name, fieldRefs.current[field_name], form);
                            } catch (err) {
                                alert("FileReader: " + err);
                            }
                        }
                    } else {
                        onChange({ prefix, apiData, setApiData, name, val: Array.from(ev.target.files) });
                    }
                } else if (field?.is_video === true && field.widget.attrs?.video_dimensions) {
                    onChange({ prefix, apiData, setApiData, name, val: Array.from(ev.target.files) });
                    const dimensionsParams = field.widget.attrs?.video_dimensions;
                    const durationValidate = field.widget.attrs?.video_duration;
                    const file = ev.target.files[0];
                    if (file) {
                        const { width, height, duration } = await getVideoInfo(file);

                        const errorMessages = [];

                        dimensionsParams.forEach((param) => {
                            if (param?.check === "max" && (width > param?.width || height > param?.height)) {
                                errorMessages.push(
                                    t("Разрешение не должно быть больше") +
                                        ` ${param?.width}x${param?.height}. ` +
                                        t("Текущее") +
                                        `: ${width}x${height}`
                                );
                            } else if (param?.check === "min" && (width < param?.width || height < param?.height)) {
                                errorMessages.push(
                                    t("Разрешение не должно быть меньше") +
                                        ` ${param?.width}x${param?.height}. ` +
                                        t("Текущее") +
                                        `: ${width}x${height}`
                                );
                            } else if (
                                param?.check === "exact" &&
                                (width !== param?.width || height !== param?.height)
                            ) {
                                errorMessages.push(
                                    t("Разрешение должно быть") +
                                        ` ${param?.width}x${param?.height}. ` +
                                        t("Текущее") +
                                        `: ${width}x${height}`
                                );
                            }
                        });

                        if (duration > durationValidate) {
                            errorMessages.push(
                                t("Длительность видео должна быть") +
                                    ` ${durationValidate} ` +
                                    t("сек") +
                                    ". " +
                                    t("Текущая") +
                                    `: ${duration.toFixed(3)}`
                            );
                        }

                        if (errorMessages.length > 0) {
                            const newData = { ...apiData };
                            const updatedObj = setProperty(newData, `forms_data.${prefix}.errors`, {
                                [name]: [errorMessages.join("<br/>")],
                            });
                            setApiData(updatedObj);
                        }
                    }
                } else {
                    onChange({ prefix, apiData, setApiData, name, val: Array.from(ev.target.files) });
                }
            };

            return (
                <div>
                    <Form.Control
                        type="file"
                        ref={(el) => (fieldRefs.current[field_name] = el)}
                        name={field_name}
                        style={{ display: "none" }}
                        onClick={handleFileClick}
                        onChange={handleFileChange}
                        isInvalid={form.errors[name]}
                        disabled={field.disabled}
                        readOnly={readOnly}
                        accept={widget.attrs.accept}
                        multiple={widget.attrs.multiple}
                    />
                    <Button
                        variant="outline-dark"
                        size="sm"
                        onClick={() => fieldRefs.current[field_name]?.click()}
                        disabled={field.disabled}
                    >
                        {t("Выбрать файл")}
                    </Button>
                    <span style={{ whiteSpace: "nowrap", marginLeft: 8 }}>
                        {selectedFileName || t("Файл не выбран")}
                    </span>
                </div>
            );
        }
        case "textarea": {
            return (
                <WrapperTextArea
                    name={field_name}
                    value={form.data[name]}
                    onChange={(ev) => {
                        onChange({ prefix, apiData, setApiData, name, val: ev.target.value });
                    }}
                    isInvalid={form.errors[name]}
                    disabled={field.disabled}
                    readOnly={readOnly}
                    placeholder={widget.attrs.placeholder}
                    minLength={field.min_length}
                    maxLength={maxLength}
                    rows={widget.attrs.rows}
                    cols={widget.attrs.cols}
                />
            );
        }
        case "number":
        case "text": {
            return (
                <WrapperTextInput
                    type={widget.input_type}
                    name={field_name}
                    value={form.data[name]}
                    onChange={(ev) => {
                        onChange({ prefix, apiData, setApiData, name, val: ev.target.value });
                    }}
                    isInvalid={form.errors[name]}
                    disabled={field.disabled}
                    readOnly={readOnly}
                    placeholder={widget.attrs.placeholder}
                    minLength={field.min_length}
                    maxLength={maxLength}
                    min={widget.attrs.min}
                    step={widget.attrs.step}
                />
            );
        }
        default:
            return (
                <div style={{ color: "red" }}>
                    FormFieldControl error for field "{name}", {JSON.stringify(field)}
                </div>
            );
    }
}

export function FormFieldLabel({ label, required, colLabel }) {
    return colLabel === null ? (
        <Form.Label>
            {label && (
                <>
                    <strong>{label}</strong>
                    {required && <span className="asteriskField">*</span>}
                </>
            )}
        </Form.Label>
    ) : (
        <Form.Label column>
            {label && (
                <>
                    {label}
                    {required && <span className="asteriskField">*</span>}
                </>
            )}
        </Form.Label>
    );
}

export function FormFieldHelpText({ help_text }) {
    return help_text ? <Form.Text muted>{parse(help_text)}</Form.Text> : null;
}

export function FormFieldErrorMessage({ error, custom = false }) {
    if (!error) {
        return null;
    }

    const err = <ParseFormErrors err={Array.isArray(error) ? error.join("<br>") : error} />;

    return custom ? (
        <div style={{ width: "100%", marginTop: "0.25rem", fontSize: "80%", color: "#dc3545" }}>{err}</div>
    ) : (
        <Form.Control.Feedback type="invalid">{err}</Form.Control.Feedback>
    );
}

export function FormFieldLenCount({ val, maxLength, disabled = true }) {
    if (maxLength && !disabled) {
        const current = val ? val.length : 0;
        const max = maxLength;
        let highlight = "";
        if (current > max) {
            highlight = "bg-danger text-white rounded px-1";
        }
        return <SimpleLenCount val={val} maxLength={max} className={highlight} />;
    }
    return null;
}

export function SimpleLenCount({ val, maxLength, className = "" }) {
    const current = val ? val.length : 0;
    return (
        <Form.Text muted>
            <span className={className}>
                {current}/{maxLength}
            </span>
        </Form.Text>
    );
}

export function FormFieldGroup({
    children,
    label,
    required = false,
    asRow = true, // Отображать label на одной строке с полем
    hideLabel = false, // Спрятать label
    colLabel = 3,
    colField = 9,
    underLabel,
    ...props
}) {
    const formGroupProps = {};
    if (asRow) {
        formGroupProps.as = Row;
    } else {
        colField = null;
        colLabel = null;
    }
    return (
        <Form.Group {...formGroupProps} {...props}>
            {!hideLabel && (
                <Col sm={colLabel}>
                    <FormFieldLabel label={label} required={required} colLabel={colLabel} />
                    {underLabel && <div className="mt-2 mr-2 float-right">{underLabel}</div>}
                </Col>
            )}
            {colField === null ? (
                children
            ) : (
                <Col sm={colField} className="my-auto">
                    {children}
                </Col>
            )}
        </Form.Group>
    );
}
